import {
  HStack,
  Icon,
  useToast,
  VStack,
  Text,
  Box,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { boleto } from 'boleto-brasileiro-validator';
import CustomButton from 'components/CustomButton';
import InputForm from 'components/InputForm';
import InputNumber from 'components/InputNumber';
import { useTwoFaHelper } from 'contexts/TwoFaCheckContext';
import useAuth from 'contexts/useAuth';
import { queryClient } from 'index';
import React from 'react';
import { useForm } from 'react-hook-form';
import { FaCamera } from 'react-icons/fa';
import { createBilletPayment, validateBillet } from 'services/api.service';
import { maskCurrency } from 'utils/currency';
import { CapacitorBarcodeScanner, CapacitorBarcodeScannerTypeHint } from '@capacitor/barcode-scanner';
import { Capacitor } from '@capacitor/core';

interface BilletResponse {
  id: string;
  integrationId: string;
  billetCode: string;
  uniqueCode: string;
  ipsbCode: string;
  ispbName: string;
  beneficiaryName: string;
  beneficiaryDocument: string;
  finalBeneficiaryName: string;
  finalBeneficiaryDocument: string;
  amount: string;
  dueDate: string;
  limitDueDate: string;
  allowChangeValue: boolean;
  discount: string;
  fee: string;
  finalAmount: string;
  minValue: string;
  maxValue: string;
  createdAt: string;
  updatedAt: string;
}

interface CreatePaymentParams {
  barcode: string;
  description: string;
  amount?: string;
}

const BarcodePayment: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const toast = useToast();
  const { updateUserData } = useAuth();
  const { requestTwoFa } = useTwoFaHelper();
  const isNative = window.innerWidth < 768 && Capacitor.isNativePlatform();
  const [billetData, setBilletData] = React.useState<BilletResponse | null>(null);

  const { control, handleSubmit, setValue, setError } = useForm<CreatePaymentParams>();
  const { mutateAsync: findBillet, isLoading: isLoadingFindBillet } = useMutation({
    mutationFn: validateBillet,
  });

  const { mutateAsync: createPayment, isLoading: isLoadingCreatePayment } = useMutation({
    mutationFn: createBilletPayment,
  });

  const handleCreatePayment = async (data: CreatePaymentParams) => {
    const twoFaId = await requestTwoFa();

    if (!twoFaId?.id) {
      return;
    }

    try {
      await createPayment({ billetId: billetData?.id, amount: Number(data.amount), twoFaId: twoFaId.id });
      updateUserData();
      onClose();
      queryClient.refetchQueries({ queryKey: ['findAllWithdrawers'] });

      toast({
        title: 'Sucesso',
        description: 'Pagamento criado com sucesso',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao criar pagamento',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleFindBillet = async (data: CreatePaymentParams) => {
    const isValid = boleto(data.barcode);
    if (!isValid) {
      setError('barcode', {
        message: 'Código de barras inválido',
      });
      toast({
        title: 'Erro',
        description: 'Código de barras inválido',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
      return;
    }
    try {
      const billet = await findBillet(data.barcode);
      setBilletData(billet);
      const canEditValue = billet.allowChangeValue && Number(billet.finalAmount) === 0;
      if (!canEditValue) {
        setValue('amount', billet.finalAmount);
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Erro ao buscar boleto',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const startScan = async () => {
    try {
      // Solicita permissão para usar a câmera

      // Inicia o scanner
      document.querySelector('body')?.classList.add('scanner-active');
      const result = await CapacitorBarcodeScanner.scanBarcode({
        hint: CapacitorBarcodeScannerTypeHint.ITF,
      });

      if (result.ScanResult) {
        setValue('barcode', result.ScanResult);
      }
    } catch (error) {
      toast({
        title: 'Erro',
        description: 'Leitura do código de barras cancelada',
        status: 'info',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      document.querySelector('body')?.classList.remove('scanner-active');
    }
  };

  return (
    <>
      <Modal
        isOpen={true}
        onClose={onClose}
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Criar pagamento de boleto</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack
              spacing={5}
              alignItems="start"
            >
              <HStack
                width="100%"
                justifyContent="space-between"
                alignItems="flex-end"
              >
                <InputForm
                  control={control}
                  label="Código de Barras"
                  name="barcode"
                  type="text"
                  isDisabled={!!billetData}
                  onChange={(value) => {
                    setValue('barcode', value);
                  }}
                  rules={{
                    required: 'Campo obrigatório',
                    minLength: {
                      value: 40,
                      message: 'Código de barras inválido',
                    },
                  }}
                />
                {isNative && (
                  <CustomButton
                    aria-label="Ler código de barras"
                    onClick={startScan}
                    w="40px"
                  >
                    <Icon as={FaCamera} />
                  </CustomButton>
                )}
              </HStack>

              <CustomButton
                w="100%"
                colorScheme="green"
                isDisabled={!!billetData}
                isLoading={isLoadingFindBillet}
                onClick={handleSubmit(handleFindBillet)}
              >
                Buscar Boleto
              </CustomButton>

              {billetData && (
                <Box width="100%">
                  <VStack
                    spacing={3}
                    align="stretch"
                  >
                    <Text>
                      <strong>Beneficiário:</strong> {billetData.beneficiaryName}
                    </Text>
                    <Text>
                      <strong>Documento do Beneficiário:</strong> {billetData.beneficiaryDocument}
                    </Text>
                    <Text>
                      <strong>Banco:</strong> {billetData.ispbName} ({billetData.ipsbCode})
                    </Text>
                    <Text>
                      <strong>Data de Vencimento:</strong> {new Date(billetData.dueDate).toLocaleDateString()}
                    </Text>
                    <Text>
                      <strong>Data Limite:</strong> {new Date(billetData.limitDueDate).toLocaleDateString()}
                    </Text>
                    <Text>
                      <strong>Valor Original:</strong> R$ {maskCurrency(+billetData.amount)}
                    </Text>
                    {Number(billetData.discount) > 0 && (
                      <Text>
                        <strong>Desconto:</strong> {maskCurrency(+billetData.discount)}
                      </Text>
                    )}
                    {Number(billetData.fee) > 0 && (
                      <Text>
                        <strong>Taxa:</strong> {maskCurrency(+billetData.fee)}
                      </Text>
                    )}

                    {billetData.allowChangeValue && Number(billetData.finalAmount) === 0 ? (
                      <>
                        {billetData.minValue && billetData.maxValue && (
                          <Text
                            fontSize="sm"
                            color="gray.600"
                          >
                            Valor permitido entre {maskCurrency(+billetData.minValue)} e{' '}
                            {maskCurrency(+billetData.maxValue)}
                          </Text>
                        )}
                        <InputNumber
                          control={control}
                          label="Valor a Pagar"
                          name="amount"
                          rules={{
                            required: 'Campo obrigatório',
                            min: {
                              value: Number(billetData.minValue),
                              message: `Valor mínimo permitido é ${maskCurrency(+billetData.minValue)}`,
                            },
                            max: {
                              value: Number(billetData.maxValue),
                              message: `Valor máximo permitido é ${maskCurrency(+billetData.maxValue)}`,
                            },
                          }}
                        />
                      </>
                    ) : (
                      <Text>
                        <strong>Valor a Pagar:</strong> {maskCurrency(+billetData.finalAmount)}
                      </Text>
                    )}
                    <CustomButton
                      w="100%"
                      colorScheme="green"
                      isLoading={isLoadingCreatePayment}
                      onClick={handleSubmit(handleCreatePayment)}
                    >
                      Criar Pagamento
                    </CustomButton>
                  </VStack>
                </Box>
              )}
            </VStack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </>
  );
};

export default BarcodePayment;
